import React, { useCallback, useContext } from 'react';

// import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Typography, Input, Switch, Paper } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import Select, { Async as AsyncSelect } from 'react-select';
import { withFirebase } from '../Firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FreightForwarderContext } from '../Session';
import {
  faExclamationTriangle,
  faExclamationCircle,
  faEye,
  faEyeSlash,
  faPlusCircle,
} from '@fortawesome/free-solid-svg-icons';

import moment from 'moment';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
// import { faSignOut } from '@fortawesome/pro-regular-svg-icons'

const styles = (theme) => ({
  chargeWrapper: {
    display: 'inline-block',
    marginRight: theme.spacing(1),
  },
  roundedInput: {
    // marginTop: theme.spacing(1),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[50]}`, //same as background so that focus doesn't chaneg overall size
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
  inputMultiline: {
    padding: theme.spacing(1),
  },
  fixedWidth: {
    width: '80px',
  },
  bottomMargin: {
    marginBottom: theme.spacing(1),
  },
  disabled: {
    cursor: 'not-allowed',
  },
  error: {
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
    borderRadius: theme.spacing(1),
  },
  errorBorder: {
    '& .MuiInputBase-input': {
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.error.main}`,
    },
  },
  focused: {
    '& .MuiInputBase-input': {
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  dateSelect: {
    '& input': {
      width: '110px',
    },
  },
});

const reactSelectSmall = (theme) => {
  return {
    control: (base, state) => {
      return {
        ...base,
        background: 'unset',
        minHeight: '37px',
        minWidth: '50px',
        borderStyle: 'none',
        // Removes weird border around container
        boxShadow: null,
      };
    },
    valueContainer: (base) => {
      return {
        padding: '0px',
      };
    },
    indicatorSeparator: (base) => ({}),
    dropdownIndicator: (base) => {
      return {
        ...base,
        padding: '0px',
      };
    },
    input: (base) => {
      return {
        ...base,
        color: theme.palette.grey[400],
      };
    },
    singleValue: (base) => ({
      ...base,
      marginLeft: 0,
      marginRight: 0,
      maxWidth: `calc(100% - ${theme.spacing(2)}px)`,
      color: theme.palette.grey[400],
    }),
  };
};

const ContainerChargeInputComp = (props) => {
  const { container, onChange, disabled, classes, value, name, currencyFieldName, currencyFieldValue, theme } = props;
  const { currentFreightForwarder } = useContext(FreightForwarderContext);
  return (
    <div key={container._id} className={classes.chargeWrapper}>
      <Typography variant="subtitle2">{container.name}</Typography>
      <Box display="flex">
        <Input
          type="number"
          value={value}
          name={name}
          disabled={disabled}
          onChange={onChange}
          disableUnderline={true}
          classes={{ input: `${classes.roundedInput} ${classes.fixedWidth}` }}
          className={classes.bottomMargin}
        />
        {currencyFieldName && currentFreightForwarder?.currencies && (
          <StyledSelect
            name={currencyFieldName}
            value={currencyFieldValue}
            getOptionLabel={(c) => c.code}
            getOptionValue={(c) => c._id}
            onChange={onChange}
            options={currentFreightForwarder.currencies}
            menuPortalTarget={document.body}
            placeholder="Currency"
            isClearable={false}
            required={true}
            styles={reactSelectSmall(theme)}
          />
        )}
      </Box>
    </div>
  );
};

const reactSelectCustomStyles = (theme, dark = true, required = false) => {
  return {
    control: (base, state) => {
      let borderStyle;
      if (required && !state.hasValue) {
        borderStyle = 'solid';
      } else if (dark) {
        borderStyle = 'none';
      } else {
        borderStyle = state.isFocused ? base.borderStyle : 'none';
      }
      let background;
      if (dark) {
        if (state.hasValue) {
          background = theme.palette.common.white;
        } else {
          background = theme.palette.primary.lighter;
        }
      } else {
        background = theme.palette.grey[200];
      }
      return {
        ...base,
        background: background,
        // match with the menu
        borderRadius: theme.spacing(1),
        // marginTop: theme.spacing(1),
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
        borderStyle: borderStyle,
        // borderRadius: state.isFocused ? "3px 3px 0 0" : 3,
        // Overwrittes the different states of border
        borderColor: state.hasValue
          ? base.borderColor
          : required && !state.isFocused
          ? theme.palette.common.danger
          : base.borderColor,
        // borderColor: state.hasValue ? "yellow" : "green",
        // Removes weird border around container
        boxShadow: null,
        '&:hover': {
          // Overwrittes the different states of border
          // borderColor: state.isFocused ? "red" : "blue"
          borderColor: state.hasValue
            ? base.borderColor
            : required && !state.isFocused
            ? theme.palette.common.danger
            : base.borderColor,
        },
      };
    },
    input: (base) => {
      let color;
      if (dark) {
        color = theme.palette.primary.light;
      } else {
        color = theme.palette.common.black;
      }
      return {
        ...base,
        color: color,
      };
    },
    singleValue: (base) => ({
      ...base,
      color: dark ? theme.palette.primary.main : theme.palette.common.black,
    }),
    multiValue: (base) => ({
      ...base,
      background: theme.palette.primary.darker,
      color: theme.palette.grey[50],
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: theme.palette.grey[50],
    }),
    menu: (base) => ({
      ...base,
      zIndex: 99, //appear overtop of stickyheader table
      // override border radius to match the box
      // borderRadius: 0,
      // kill the gap
      // marginTop: 0
    }),
    menuList: (base) => ({
      ...base,
      // kill the white space on first and last option
      // padding: 0
    }),
    indicatorsContainer: (base) => ({
      ...base,
      '& > div': {
        padding: dark ? base.padding : theme.spacing(0.5),
      },
    }),
    menuPortal: (base, state) => {
      return {
        ...base,
        minWidth: 200,
        zIndex: 9999,
        // override border radius to match the box
        // borderRadius: 0,
        // kill the gap
        // marginTop: 0
      };
    },
  };
};

const StyledAsyncSelectComp = (props) => {
  // do not pass down the theme, as it overrrides the default theme on AsyncSelect
  const { theme, loadOptions, debounceDelay = 500, dark = false, required, ...otherProps } = props;
  const debouncedFetch = debounce((searchTerm, callback) => {
    loadOptions(searchTerm)
      .then((result) => {
        return callback(result);
      })
      .catch((error) => callback(error, null));
    // Explicitly not returning a Promise. https://github.com/JedWatson/react-select/issues/3075
    return;
  }, debounceDelay);

  return (
    <AsyncSelect
      loadOptions={debouncedFetch}
      cacheOptions
      defaultOptions
      isClearable={true}
      styles={reactSelectCustomStyles(theme, dark, required)}
      {...otherProps}
    />
  );
};

class AsyncCountrySelectComp extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      valueObj: '',
    };

    this.countryField = props.countryField;
  }

  componentWillUnmount() {
    if (this.source) {
      this.source.cancel('AsyncCountrySelectComp Operation canceled by the user.');
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      this.getCountries('');
    }
  }

  getCountries = async (textQuery) => {
    const countries = [];
    return this.props.firebase.getCachedCountries(textQuery).then((response) => {
      countries.push(...response);
      const valueObj = countries.find((c) => isEqual(this.countryField ? c[this.countryField] : c, this.props.value));
      this.setState({ valueObj });
      return countries;
    });
  };
  returnPseudoEvent = (selection, action) => {
    this.setState({ valueObj: selection });
    let value;
    if (selection) {
      value = this.countryField ? selection[this.countryField] : selection;
    } else {
      value = '';
    }
    return this.props.onChange({ target: { name: action.name, value: value } });
  };

  render() {
    const { value, onChange, ...otherProps } = this.props;
    const { valueObj } = this.state;
    return (
      <StyledAsyncSelect
        debounceDelay={0}
        cacheOptions
        defaultOptions
        value={valueObj}
        getOptionValue={(option) => (this.countryField ? option[this.countryField] : option)}
        getOptionLabel={(option) => `${option.name} (${option.code})`}
        onChange={this.returnPseudoEvent}
        loadOptions={(textQuery) => this.getCountries(textQuery)}
        isClearable={true}
        {...otherProps}
      />
    );
  }
}

class AsyncPortSelectComp extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      valueObj: '',
    };

    this.portField = props.portField;
  }

  componentWillUnmount() {
    if (this.source) {
      this.source.cancel('AsyncPortSelectComp Operation canceled by the user.');
    }
  }

  getPorts = async (textQuery) => {
    const ports = [];
    const { where = {} } = this.props;
    const value = this.props.isMulti === true ? this.props.value.replace(/\s/g, '').split(',') : this.props.value;
    const curPortsQuery = {};
    const exclCurPortsQuery = {};
    const locationToPortObj = (location) => {
      return { ...location.port, _id: location._id, code_5: `${location.country.code}${location.port.code}` };
    };
    if (textQuery === '' && value && isEmpty(value) === false) {
      // get the current current port so it displays correctly
      if (this.portField) {
        if (this.props.isMulti === true) {
          if (this.portField === 'code_5') {
            if (value && value.some((v) => v.length > 0)) {
              curPortsQuery.$or = [];
              exclCurPortsQuery.$and = [];

              value.forEach((v) => {
                if (v.length > 0) {
                  const countryCode = v.substr(0, 2);
                  const portCode = v.substr(-3, 3);
                  curPortsQuery.$or.push({
                    'port.code': portCode,
                    'country.code': countryCode,
                  });
                  exclCurPortsQuery.$and.push({
                    'port.code': { $ne: portCode },
                    // 'country.code': { $ne: countryCode }
                  });
                }
              });
            }
          } else if (this.portField === '_id') {
            curPortsQuery['_id'] = { $in: value };
            exclCurPortsQuery['_id'] = { $nin: value };
          } else {
            curPortsQuery[`port.${this.portField}`] = { $in: value };
            exclCurPortsQuery[`port.${this.portField}`] = { $nin: value };
          }
        } else {
          if (this.portField === 'code_5') {
            const countryCode = value.substr(0, 2);
            const portCode = value.substr(-3, 3);
            curPortsQuery['port.code'] = portCode;
            curPortsQuery['country.code'] = countryCode;
            exclCurPortsQuery['port.code'] = { $ne: portCode };
          } else if (this.portField === '_id') {
            curPortsQuery['_id'] = value;
            exclCurPortsQuery['_id'] = { $ne: value };
          } else {
            curPortsQuery[`port.${this.portField}`] = value;
            exclCurPortsQuery[`port.${this.portField}`] = { $ne: value };
          }
        }
      } else {
        if (this.props.isMulti === true) {
          curPortsQuery['_id'] = { $in: value.map((v) => v._id) };
          exclCurPortsQuery['_id'] = { $nin: value.map((v) => v._id) };
        } else {
          curPortsQuery['_id'] = value['_id'];
          exclCurPortsQuery['_id'] = { $ne: value['_id'] };
        }
      }
      const response = await this.props.firebase.locations(
        {
          ...where,
          ...curPortsQuery,
        },
        { portsOnly: true }
      );
      ports.push(...response.locations.map((l) => locationToPortObj(l)));
    }
    this.source = this.props.firebase.createSource();
    return this.props.firebase
      .locations(
        {
          ...where,
          ...exclCurPortsQuery,
        },
        { portsOnly: true, textQuery: textQuery },
        this.source
      )
      .then((response) => {
        ports.push(...response.locations.map((l) => locationToPortObj(l)));
        let valueObj;
        if (this.props.isMulti === true && value) {
          valueObj = this.state.valueObj || []; //so we dont clear any existing values
          if (this.portField) {
            valueObj.push(
              ...ports.filter((p) => {
                return value.includes(p[this.portField]) && !valueObj.some((v) => v[this.portField]);
              })
            );
          } else {
            valueObj.push(...ports.filter((p) => value.find((currentPort) => isEqual(currentPort, p) !== undefined)));
          }
        } else {
          valueObj = ports.find((p) => isEqual(this.portField ? p[this.portField] : p, value));
        }
        this.setState({ valueObj });
        return ports;
      });
  };

  returnPseudoEvent = (selection, action) => {
    this.setState({ valueObj: selection });
    let value;
    if (selection) {
      if (this.props.isMulti === true) {
        value = this.portField ? selection.map((s) => s[this.portField]) : selection;
      } else {
        value = this.portField ? selection[this.portField] : selection;
      }
    } else {
      value = '';
    }
    return this.props.onChange({ target: { name: action.name, value: value, fullPort: selection } });
  };

  render() {
    const { value, onChange, ...otherProps } = this.props;
    const { valueObj } = this.state;
    return (
      <StyledAsyncSelect
        cacheOptions
        defaultOptions
        value={valueObj}
        getOptionValue={(option) => (this.portField ? option[this.portField] : option)}
        getOptionLabel={(option) => `${option.name} (${option.code})`}
        onChange={this.returnPseudoEvent}
        loadOptions={(textQuery) => this.getPorts(textQuery)}
        isClearable={true}
        {...otherProps}
      />
    );
  }
}

const StyledSelectComp = (props) => {
  // do not pass down the theme, as it overrrides the default theme on AsyncSelect
  const {
    theme,
    dark,
    required,
    value,
    name,
    options,
    getOptionValue,
    isMulti,
    onChange,
    selectRef,
    styles = {},
    ...otherProps
  } = props;

  const toValueObject = useCallback(
    (v) => {
      if (isMulti === true) {
        return options ? options.filter((o) => v.includes(getOptionValue(o))) : [];
      } else {
        return options ? options.find((o) => getOptionValue(o) === v) : '';
      }
    },
    [getOptionValue, options, isMulti]
  );
  const [valueObj, setValueObj] = React.useState(toValueObject(value));

  React.useEffect(() => {
    // setValueObj(props.value);
    setValueObj(toValueObject(props.value));
  }, [props.value, toValueObject]);

  const returnPseudoEvent = (selection, action) => {
    setValueObj(selection);
    let value;
    if (selection) {
      if (props.isMulti === true) {
        value = selection.map((s) => getOptionValue(s));
      } else {
        value = getOptionValue(selection);
      }
    } else {
      value = '';
    }
    return props.onChange({ target: { name: action.name, value: value } });
  };

  return (
    <Select
      ref={selectRef}
      name={name}
      value={valueObj}
      options={options}
      getOptionValue={getOptionValue}
      onChange={returnPseudoEvent}
      styles={{ ...reactSelectCustomStyles(theme, dark, required), ...styles }}
      isMulti={isMulti}
      {...otherProps}
    />
  );
};

StyledSelectComp.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  options: (props, propName, componentName) => {
    if (!(props[propName] && props[propName].length > 0)) {
      return new Error('Options must be an array of length greater than 0');
    }
  },
  getOptionValue: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

const RoundedInputComp = React.memo(function RoundedInputComp(props) {
  // const RoundedInputComp = (props) => {
  const { classes, className, fullWidth, bottomMargin = true, ...otherProps } = props;
  /*
        The `...otherProps` will assin all props other than classes.
        If we were to assign all props including 'classes' it overwrites the changes we have made
    */

  let dynamnicClasses = '';
  if (props.required && !(props.value !== null && props.value !== undefined && props.value.toString().length > 0)) {
    dynamnicClasses = classes.errorBorder;
  }
  // regarding "new-password"
  // see: https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
  return (
    <Input
      autoComplete="new-password"
      disableUnderline={true}
      fullWidth={fullWidth === undefined ? true : fullWidth}
      classes={{
        focused: classes.focused,
        input: classes.roundedInput,
        disabled: classes.disabled,
        error: classes.errorBorder,
        inputMultiline: classes.inputMultiline,
      }}
      className={`${className} ${bottomMargin && classes.bottomMargin} ${dynamnicClasses}`}
      {...otherProps}
    />
  );
});

const RoundedDatePickerComp = React.forwardRef((props, ref) => {
  const { value, autoOk = true, classes, className, onChange, ...otherProps } = props;
  /*
        The `...otherProps` will assin all props other than classes.
        If we were to assign all props including 'classes' it complains
    */
  const renderInput = (props) => {
    const { onClick, value, inputProps, error, required, endAdornment } = props;
    return (
      <RoundedInput
        bottomMargin={false}
        className={className}
        type="text"
        onClick={onClick}
        value={value}
        endAdornment={endAdornment}
        inputProps={inputProps}
        error={error}
        required={required}
      />
    );
  };

  const handleOnChange = (moment) => {
    // set the start of day as otherwise the time saved is inconsistent
    if (moment) {
      moment.startOf('day');
    }
    // then call the onChange pass in via props
    onChange(moment);
  };

  return (
    <DatePicker
      value={value || null}
      autoOk={autoOk}
      classes={classes}
      TextFieldComponent={renderInput}
      onChange={handleOnChange}
      clearable={true}
      {...otherProps}
    />
  );
});

RoundedDatePickerComp.displayName = 'RoundedDatePickerComp';

export const DateWarning = withStyles((theme) => ({
  iconWarning: {
    color: theme.palette.common.orange,
    padding: theme.spacing(1),
  },
}))((props) => {
  const { date, compareDate, classes } = props;
  let show = false;
  if (date && compareDate && new Date(date) > new Date(compareDate)) {
    show = true;
  }

  return show ? (
    <FontAwesomeIcon
      title="Date is after Port Pair Valid To date"
      className={classes.iconWarning}
      icon={faExclamationTriangle}
    />
  ) : null;
});

ContainerChargeInputComp.propTypes = {
  container: PropTypes.object.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const CustomSwitch = withStyles((theme) => ({
  root: {
    width: 60,
    height: 40,
    padding: 2,
    display: 'flex',
  },
  switchBase: {
    padding: 4,
    color: theme.palette.grey[500],
    '&$checked': {
      color: theme.palette.common.white,
      '& + $track': {
        opacity: 1,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  thumb: {
    width: 32,
    height: 32,
    boxShadow: 'none',
  },
  track: {
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: 32,
    opacity: 1,
    backgroundColor: theme.palette.common.white,
  },
  checked: {},
}))(Switch);

/*
Example usage if you alse need a label:

<Grid component="label" container alignItems="center" spacing={1}>
    <Grid item>
        <CustomSwitch
            checked={this.state.checkedC}
            onChange={this.handleChange('checkedC')}
            value="checkedC"
        />
    </Grid>
    <Grid item>Would You like to include charges?</Grid>
</Grid>

*/

// export default withFirebase(withStyles(styles)(ContainerChargeInput));
// export default compose(
//     withStyles(styles)
// )(ContainerChargeInput);

const ErrorsComp = (props) => {
  const { errors, classes, formValid } = props;
  return (
    <React.Fragment>
      {Object.keys(errors).length === 0 && formValid !== true && (
        <Paper className={classes.error}>Form Invalid, please check all fields</Paper>
      )}
      {Object.entries(errors).map(([k, v]) => {
        return (
          <Paper key={k} className={classes.error}>
            {v}
          </Paper>
        );
      })}
    </React.Fragment>
  );
};

const WarningIconComp = ({ classes, validUntil, titlePrefix, className }) => {
  let expiredClasses, icon, title;
  title = titlePrefix && titlePrefix.length > 0 ? `${titlePrefix} ` : '';
  if (validUntil) {
    validUntil = moment(validUntil); //convert to moment

    const now = moment();
    const sevenDaysFromNow = moment().add(7, 'days');

    if (now.isAfter(validUntil)) {
      expiredClasses = classes.iconExpired;
      icon = faExclamationTriangle;
      title += 'Expired';
    } else if (sevenDaysFromNow.isAfter(validUntil)) {
      expiredClasses = classes.iconWarning;
      icon = faExclamationCircle;
      title += 'Expires in less than 7 days';
    }
  }
  return expiredClasses ? (
    <FontAwesomeIcon title={title} className={`${expiredClasses} ${className}`} icon={icon} />
  ) : null;
};

export const ClientDisplayToggle = withStyles((theme) => {
  return {
    muted: {
      color: theme.palette.grey[400],
      fontWeight: 'normal',
    },
    toggleEye: {
      marginTop: '-3px',
      marginLeft: '3px',
    },
  };
})(({ buttonClassses, classes, onClick, visible }) => {
  if (buttonClassses === undefined) {
    buttonClassses = classes.toggleEye;
  }

  return (
    <IconButton className={buttonClassses} size="small" aria-label="toggle Grand Total visibility" onClick={onClick}>
      {visible === true ? (
        <FontAwesomeIcon title="Shown to customer" icon={faEye} />
      ) : (
        <FontAwesomeIcon title="Not shown to customer" icon={faEyeSlash} className={classes.muted} />
      )}
    </IconButton>
  );
});

export const DateSelect = withStyles(styles)((props) => {
  const { fieldName, value, onChange, compareDate, error, classes } = props;
  const handleChange = (date) => {
    onChange(date);
  };
  return (
    <React.Fragment>
      <Typography variant="caption" color="primary" gutterBottom className="d-block">
        {fieldName}
      </Typography>
      <RoundedDatePicker
        className={classes.dateSelect}
        endAdornment={<DateWarning date={value} compareDate={compareDate} />}
        error={!!error}
        required={true}
        value={value}
        onChange={handleChange}
      />
    </React.Fragment>
  );
});

export const NewLineButton = withStyles((theme) => ({
  outsideLeft: {
    color: theme.palette.primary.main,
    position: 'absolute',
    marginLeft: theme.spacing(-4),
    cursor: 'pointer',
  },
  newLineButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
    justifyContent: 'left',
    borderRadius: '0px',
  },
}))((props) => {
  const { buttonText, classes, ...otherProps } = props;
  return (
    <Button className={classes.newLineButton} {...otherProps}>
      <FontAwesomeIcon icon={faPlusCircle} size="lg" className={classes.outsideLeft} />
      {buttonText}
    </Button>
  );
});

export const ContainerChargeInput = compose(withStyles(styles, { withTheme: true }))(ContainerChargeInputComp);

export const StyledAsyncSelect = compose(withStyles(styles, { withTheme: true }))(StyledAsyncSelectComp);

export const AsyncCountrySelect = compose(withFirebase)(AsyncCountrySelectComp);

export const AsyncPortSelect = compose(withFirebase)(AsyncPortSelectComp);

export const StyledSelect = compose(withStyles(styles, { withTheme: true }))(StyledSelectComp);

export const RoundedInput = compose(withStyles(styles))(RoundedInputComp);

export const Errors = compose(withStyles(styles))(ErrorsComp);

export const RoundedDatePicker = compose(withStyles(styles))(RoundedDatePickerComp);

export const WarningIcon = compose(
  withStyles((theme) => {
    return {
      iconExpired: {
        color: theme.palette.common.danger,
        padding: theme.spacing(1),
      },
      iconWarning: {
        color: theme.palette.common.orange,
        padding: theme.spacing(1),
      },
    };
  })
)(WarningIconComp);
